import React, { useEffect, useState } from "react";
import { adminAxios, axiosInstance } from "../../axiosInstance";
import loader from "../../assets/loader.webp";
import useDefaultCurrency from "../../utility/useDefaultCurrency";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";
import DismissableBanner from "./DismissableBanner";
import { NavLink } from "react-router";

// All periods available
const DATE_FILTERS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "year", label: "This year" }
];

function getPreviousPeriodLabel(key) {
  switch (key) {
    case "today":
      return "yesterday";
    case "week":
      return "last week";
    case "month":
      return "last month";
    case "year":
      return "last year";
    default:
      return "";
  }
}

function getPeriodDisplay(key) {
  switch (key) {
    case "today":
      return "today";
    case "week":
      return "this week";
    case "month":
      return "this month";
    case "year":
      return "this year";
    default:
      return "";
  }
}

// Helper to parse ₦-style numbers safely
function parseCurrencyString(val = "") {
  // Remove commas and all non-numeric except '.' and minus
  if (!val) return 0;
  const number = val.replace(/[^0-9.-]/g, ""); // Remove ₦ and commas
  return Number(number) || 0;
}

function Home() {
  const { data } = useDefaultCurrency("admin");
  const [currency, setCurrency] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [, setOverviewData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  // Multiple period filters supported by API
  const [selectedPeriod, setSelectedPeriod] = useState("today");

  // Holds the whole performance data response from API
  const [performanceData, setPerformanceData] = useState(null);

  // Summary calculated from performanceData + selectedPeriod
  const [summary, setSummary] = useState({
    revenue: 0,
    prevRevenue: 0,
    loaded: false
  });

  // Fetch performance stats ONCE
  async function fetchPerformanceStatsOnce() {
    try {
      const token = sessionStorage.getItem("admin_token");
      // Always fetch without filter, since all periods come in response
      const { data: res } = await axiosInstance.get(
        `/api/performance/stats`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined
          }
        }
      );
      if (res?.error) throw new Error(res.message || "Failed to fetch performance data");
      setPerformanceData(res?.data || {});
    } catch (err) {
      setPerformanceData({});
      console.log("Error fetching performance data", err);
    }
  }

  // When performanceData or selectedPeriod changes, recompute summary
  useEffect(() => {
    if (!performanceData) return;
    let periodData = performanceData[selectedPeriod] || {};
    let revenue = 0, prevRevenue = 0;
    switch (selectedPeriod) {
      case "today":
        revenue = parseCurrencyString(periodData.today_total_sales);
        prevRevenue = parseCurrencyString(periodData.yesterday_total_sales);
        break;
      case "week":
        revenue = parseCurrencyString(periodData.this_week_total_sales);
        prevRevenue = parseCurrencyString(periodData.last_week_total_sales);
        break;
      case "month":
        revenue = parseCurrencyString(periodData.this_month_total_sales);
        prevRevenue = parseCurrencyString(periodData.last_month_total_sales);
        break;
      case "year":
        revenue = parseCurrencyString(periodData.this_year_total_sales);
        prevRevenue = parseCurrencyString(periodData.last_year_total_sales);
        break;
      default:
        revenue = 0;
        prevRevenue = 0;
    }
    setSummary({
      revenue,
      prevRevenue,
      loaded: true
    });
  }, [performanceData, selectedPeriod]);

  // Fetch both overview and (once) performance summary
  useEffect(() => {
    let ignore = false;
    setIsLoading(true);

    async function fetchData() {
      try {
        // Get access token from sessionStorage
        const adminToken = sessionStorage.getItem("admin_token");
        const response = await adminAxios.get(
          "dashboard",
          {
            headers: {
              Authorization: adminToken ? `Bearer ${adminToken}` : undefined
            }
          }
        );
        if (!ignore) {
          setOverviewData(response?.data?.data || {});
        }
      } catch (err) {
        if (!ignore) setOverviewData({});
        console.log("Error fetching overview data", err);
      }
    }

    fetchData();
    fetchPerformanceStatsOnce();
    handleCheckUserRole();
    setTimeout(() => setIsLoading(false), 350);

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line
  }, []);

  // Check user role for admin/manager
  const handleCheckUserRole = () => {
    const userInfo = sessionStorage.getItem("token");
    const newUser = JSON.parse(userInfo);
    setIsAdmin(newUser && (newUser.role === "Admin" || newUser.role === "Manager"));
  };

  // Set currency from default currency hook
  useEffect(() => {
    setCurrency(data?.currency?.symbol || "");
  }, [data]);

  if (isLoading || !summary.loaded) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <img src={loader} alt="loader" className="w-10 lg:w-20" />
      </div>
    );
  }

  // --- CHART DATA: summary only ---
  const { revenue, prevRevenue } = summary;
  const revenueDiff = revenue - prevRevenue;
  const absRevenueDiff = Math.abs(revenueDiff);
  const periodPhrase = getPeriodDisplay(selectedPeriod);
  const prevPeriodPhrase = getPreviousPeriodLabel(selectedPeriod);

  let diffSentence = "";

  if (revenueDiff === 0) {
    diffSentence = `That's the same as ${prevPeriodPhrase}.`;
  } else if (revenueDiff > 0) {
    diffSentence = `That's ${currency}${formatNumbersWithDecimal(absRevenueDiff)} more than ${prevPeriodPhrase}.`;
  } else {
    diffSentence = `That's ${currency}${formatNumbersWithDecimal(absRevenueDiff)} less than ${prevPeriodPhrase}.`;
  }

  return (
    <>
      <div className="flex flex-col lg:justify-between gap-6 bg-[#F0F1F3]">
        {/* PERFORMANCE SUMMARY BLOCK ONLY */}
        <div className="flex flex-col bg-white rounded-lg gap-6 py-12 p-4 mb-6 font-inter">
          {/* Date Range Filter Buttons */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full mb-2 font-inter">
            <div className="flex flex-row gap-2 mb-4 lg:mb-0 font-inter">
              {DATE_FILTERS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setSelectedPeriod(key)}
                  className={`rounded-full px-4 py-1.5 font-inter font-semibold text-sm transition-colors border-2 ${
                    selectedPeriod === key
                      ? "bg-blue-600 text-white border-blue-700 shadow-md"
                      : "bg-transparent text-blue-700 border-blue-300 hover:bg-blue-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* SIMPLE 3-SENTENCE UPDATE */}
          <div className="mb-2 font-inter">
            <h4 className="text-2xl font-medium mb-2 text-black font-inter">
              A quick update on your business performance
            </h4>
            <br />
            <div className="text-black text-2xl mb-2 mt-2 font-medium font-inter">
              So far {periodPhrase}, your business has made
            </div>
            <div className="flex flex-wrap gap-3 items-center my-2 font-inter">
              {/* Main Revenue Number */}
              <span className="text-2xl font-black text-black flex items-center font-inter">
                {currency}
                {formatNumbersWithDecimal(revenue)}
              </span>
            </div>
            <div className="text-black text-xl mt-1 font-normal italic font-inter">
              {diffSentence}
            </div>
            <div className="mt-4 font-inter">
              <NavLink
                to="/admin/reports"
                className="font-inter font-semibold pl-4 text-blue-600 hover:underline text-base flex items-center justify-start"
                style={{ width: "fit-content" }}
              >
                view detailed report <span className="ml-1 text-lg">➔</span>
              </NavLink>
            </div>
          </div>
        </div>
        {isAdmin && <DismissableBanner />}
      </div>
    </>
  );
}

export default Home;